import { Alert, Anchor, Button, Code, Divider, Stack, Text } from '@mantine/core';
import { Link } from 'react-router-dom';
import type {
  DocumentationRemediation,
  ReleaseDocumentDetail,
  ReleaseDocumentSummary,
} from '@/types/automationReleases.ts';
import { SuiteMarkdown } from '@/pages/AutomationSuites/SuiteShared.tsx';
import { getHttpErrorMessage } from '@/services/httpRetry.ts';

type Props = {
  releaseId: string;
  documents: ReleaseDocumentSummary;
  detail?: ReleaseDocumentDetail;
  showDocuments: boolean;
  loadingDocuments?: boolean;
  documentsError?: unknown;
  onToggleDocuments: () => void;
  onRetryDocuments?: () => void;
  compact?: boolean;
};

const remediationCopy: Record<
  DocumentationRemediation['code'],
  { title: string; description: string }
> = {
  suite_blueprint_missing: {
    title: 'Suite Blueprint is missing',
    description: 'Add the Suite Blueprint, then prepare a new Release candidate.',
  },
  member_blueprint_missing: {
    title: 'Member Blueprint is missing',
    description: 'Document this exact Automation Version, then prepare a new Release candidate.',
  },
  member_blueprint_behavior_changed: {
    title: 'Member Blueprint needs behavior review',
    description:
      'Review and update this exact Automation Version’s Blueprint, then prepare a new Release candidate.',
  },
  member_blueprint_policy_resource_changed: {
    title: 'Member Blueprint policy resources changed',
    description:
      'Review the exact version’s policy resources and Blueprint, then prepare a new Release candidate.',
  },
  member_blueprint_policy_resource_unavailable: {
    title: 'Member Blueprint policy resource is unavailable',
    description:
      'Resolve or review the exact version’s policy resources and Blueprint, then prepare a new Release candidate.',
  },
};

function RemediationAction({ remediation }: { remediation: DocumentationRemediation }) {
  if (remediation.code === 'suite_blueprint_missing' && remediation.suite_id)
    return (
      <Anchor component={Link} to={`/automation-suites/${remediation.suite_id}?tab=blueprint`}>
        Open Suite Blueprint
      </Anchor>
    );
  if (remediation.automation_id && remediation.automation_version_id)
    return (
      <Anchor
        component={Link}
        to={`/automations/${remediation.automation_id}/editor?blueprintVersion=${remediation.automation_version_id}`}
      >
        Open exact Version Blueprint
      </Anchor>
    );
  return null;
}

export default function ReleaseDocumentationReview({
  releaseId,
  documents,
  detail,
  showDocuments,
  loadingDocuments = false,
  documentsError,
  onToggleDocuments,
  onRetryDocuments,
  compact = false,
}: Props) {
  const suiteAdvanced =
    documents.suite_blueprint_revision_id !== null &&
    documents.working_suite_blueprint_revision_id !== null &&
    documents.suite_blueprint_revision_id !== documents.working_suite_blueprint_revision_id;
  const detailDigestMismatch =
    detail !== undefined && detail.documentation_digest !== documents.documentation_digest;
  return (
    <Stack gap="sm">
      <Divider label={compact ? 'Documentation' : 'Release documentation'} labelPosition="left" />
      <Text size="sm">
        {compact
          ? 'Review the documentation selected for this draft before publishing.'
          : 'This Release is attached to these exact immutable Blueprint revisions. Working Blueprint changes do not alter this candidate.'}
      </Text>
      {!compact && (
        <>
          <Text size="sm">
            Release <Code>{releaseId}</Code> · documentation digest{' '}
            <Code>{documents.documentation_digest}</Code>
          </Text>
          {documents.suite_blueprint_revision_id ? (
            <Text size="sm">
              Suite Blueprint <Code>{documents.suite_blueprint_revision_id}</Code> · hash{' '}
              <Code>{documents.suite_body_sha256}</Code>
            </Text>
          ) : (
            <Text size="sm" c="dimmed">
              No Suite Blueprint is attached to this Release.
            </Text>
          )}
          {documents.member_documents.map((document) => (
            <Text key={document.automation_version_id} size="sm">
              Automation Version <Code>{document.automation_version_id}</Code> · Blueprint{' '}
              {document.blueprint_revision_id ? (
                <Code>{document.blueprint_revision_id}</Code>
              ) : (
                'not attached'
              )}
              {document.documentation_event_id ? (
                <>
                  {' · '}event <Code>{document.documentation_event_id}</Code>
                </>
              ) : null}
              {document.body_sha256 ? (
                <>
                  {' · '}hash <Code>{document.body_sha256}</Code>
                </>
              ) : null}
            </Text>
          ))}
        </>
      )}

      {!compact && suiteAdvanced && (
        <Alert color="blue" title="Current working Suite Blueprint has advanced">
          The current Suite Blueprint is{' '}
          <Code>{documents.working_suite_blueprint_revision_id}</Code>. This Release remains
          attached to <Code>{documents.suite_blueprint_revision_id}</Code>. This is advisory; it
          does not modify this candidate or require rebuilding it.
        </Alert>
      )}

      {!documents.complete && (
        <Stack gap="xs">
          <Text fw={600}>Documentation issues requiring action</Text>
          {documents.documentation_remediations.map((remediation, index) => {
            const copy = remediationCopy[remediation.code];
            return (
              <Alert
                key={`${remediation.code}-${remediation.automation_version_id ?? index}`}
                color="orange"
              >
                <Text fw={600}>{copy.title}</Text>
                <Text size="sm">{copy.description}</Text>
                <RemediationAction remediation={remediation} />
              </Alert>
            );
          })}
          {documents.blockers.map((blocker) => (
            <Alert key={blocker} color="orange">
              {blocker}
            </Alert>
          ))}
        </Stack>
      )}

      <Button variant="default" loading={loadingDocuments} onClick={onToggleDocuments}>
        {showDocuments
          ? compact
            ? 'Hide documentation'
            : 'Hide exact selected documents'
          : compact
            ? 'Read documentation'
            : 'Read exact selected documents'}
      </Button>
      {showDocuments && Boolean(documentsError) && (
        <Alert color="red" title="Exact Release documents unavailable">
          {getHttpErrorMessage(documentsError)}
          {onRetryDocuments && (
            <Button variant="subtle" disabled={loadingDocuments} onClick={onRetryDocuments}>
              Retry
            </Button>
          )}
        </Alert>
      )}
      {showDocuments && !documentsError && detailDigestMismatch && (
        <Alert color="red" title="Exact Release documents unavailable">
          Documents do not confirm the exact selected Release.
          {onRetryDocuments && (
            <Button variant="subtle" disabled={loadingDocuments} onClick={onRetryDocuments}>
              Retry
            </Button>
          )}
        </Alert>
      )}
      {showDocuments && !documentsError && !detailDigestMismatch && detail && (
        <Stack gap="md">
          {detail.suite_body && (
            <Stack gap="xs">
              <Text component="h3" size="sm" fw={600}>
                {compact ? 'Selected documentation' : 'Exact Suite Blueprint'}
              </Text>
              <SuiteMarkdown body={detail.suite_body} />
            </Stack>
          )}
          {detail.member_documents.map((document) => (
            <Stack key={document.automation_version_id} gap="xs">
              <Text component="h3" size="sm" fw={600}>
                {compact ? (
                  'Selected documentation'
                ) : (
                  <>
                    Exact Version Blueprint · <Code>{document.automation_version_id}</Code>
                  </>
                )}
              </Text>
              {document.body ? (
                <SuiteMarkdown body={document.body} />
              ) : (
                <Text size="sm" c="dimmed">
                  No Blueprint body is attached for this exact version.
                </Text>
              )}
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
